import { apiFetch } from './client';

export type EmailConfigBody =
  | { provider: 'sendgrid'; credentials: { apiKey: string; from: string }; allow_reseller_use?: boolean }
  | { provider: 'gmail_smtp'; credentials: { email: string; appPassword: string }; allow_reseller_use?: boolean }
  | {
      provider: 'smtp';
      credentials: { host: string; port: number; secure: boolean; user: string; password: string; from: string };
      allow_reseller_use?: boolean;
    }
  | { provider: null };

export interface EmailConfigView {
  provider: 'sendgrid' | 'gmail_smtp' | 'smtp' | null;
  credentials_redacted: Record<string, string> | null;
  allow_reseller_use: boolean;
}

export interface AwsPollyView {
  configured: boolean;
  region: string | null;
  access_key_id_redacted: string | null;
}

export type AwsPollyBody =
  | { access_key_id: string; secret_access_key: string; region: string }
  | { clear: true };

export interface PlatformStorageView {
  configured: boolean;
  provider: 'r2' | 's3' | null;
  account_id: string | null;
  endpoint: string | null;
  region: string | null;
  bucket: string | null;
  access_key_id_redacted: string | null;
}

export type PlatformStorageBody =
  | {
      provider: 'r2' | 's3';
      account_id?: string | null;
      access_key_id: string;
      secret_access_key: string;
      endpoint: string;
      region: string;
      bucket: string;
    }
  | { clear: true };

export interface StorageVerifyResult {
  ok: boolean;
  error?: string;
  provider?: string;
  bucket?: string;
}

export interface CnamView {
  configured: boolean;
  provider: 'none' | 'telnyx';
  api_key_redacted: string | null;
}

export type CnamBody =
  | { provider: 'telnyx'; api_key: string }
  | { provider: 'none' }
  | { clear: true };

export interface TtsCacheStats {
  entries: number;
  total_use_count: number;
  most_used: Array<{ text: string; use_count: number; language: string }>;
}

export const platformSettings = {
  getEmail: () => apiFetch<EmailConfigView>('/api/platform-settings/email'),
  putEmail: (body: EmailConfigBody) =>
    apiFetch<{ ok: true }>('/api/platform-settings/email', { method: 'PUT', body: JSON.stringify(body) }),
  testEmail: () =>
    apiFetch<{
      verify: { ok: boolean; error?: string; kind: string };
      sent: { providerKind: string; providerMessageId?: string; ok: boolean } | null;
    }>('/api/platform-settings/email/test', { method: 'POST' }),
  listContactSubmissions: (params: { handled?: boolean } = {}) => {
    const q = new URLSearchParams();
    if (params.handled !== undefined) q.set('handled', String(params.handled));
    return apiFetch<{ rows: Array<{
      id: string; name: string; email: string; message: string; handled_at: string | null; created_at: string;
    }> }>(`/api/contact-submissions?${q}`);
  },
  markContactHandled: (id: string) =>
    apiFetch<{ ok: true }>(`/api/contact-submissions/${id}/handled`, { method: 'POST' }),
  getAwsPolly: () => apiFetch<AwsPollyView>('/api/platform-settings/aws-polly'),
  putAwsPolly: (body: AwsPollyBody) =>
    apiFetch<{ ok: true }>('/api/platform-settings/aws-polly', { method: 'PUT', body: JSON.stringify(body) }),
  getStorage: () => apiFetch<PlatformStorageView>('/api/platform-settings/storage'),
  putStorage: (body: PlatformStorageBody) =>
    apiFetch<{ ok: true; active_driver: 'filesystem' | 's3' }>(
      '/api/platform-settings/storage',
      { method: 'PUT', body: JSON.stringify(body) },
    ),
  verifyStorage: () =>
    apiFetch<StorageVerifyResult>('/api/platform-settings/storage/verify', { method: 'POST' }),
  getTtsCacheStats: () =>
    apiFetch<TtsCacheStats>('/api/platform-settings/tts-cache-stats'),
  getCnam: () => apiFetch<CnamView>('/api/platform-settings/cnam'),
  putCnam: (body: CnamBody) =>
    apiFetch<{ ok: true }>('/api/platform-settings/cnam', { method: 'PUT', body: JSON.stringify(body) }),
};
